import React, { useEffect, useState, useCallback } from 'react';
import { getAuditLogs } from '../services/apiService';
import {
    Search, Filter, Eye, Terminal, Calendar, Hash, X,
    ChevronLeft, ChevronRight, RefreshCcw, ChevronDown
} from 'lucide-react';
import { format } from 'date-fns/format';
import { it } from 'date-fns/locale/it';
import { Modal } from '../components/Modal';

type Riferimento = { tipo: string; id: number; etichetta: string; eliminato?: boolean };
type Campo = { etichetta: string; valore: string };

// Codici dei riferimenti usati dal server (colonna riferimenti del registro).
const TIPI_RIF: { value: string; label: string }[] = [
    { value: 't', label: 'Turno' },
    { value: 'i', label: 'Incarico' },
    { value: 'p', label: 'Mandato' },
    { value: 's', label: 'Servizio' },
    { value: 'c', label: 'Cliente' },
    { value: 'v', label: 'Preventivo' },
];

const CATEGORIE: { value: string; label: string }[] = [
    { value: '', label: 'Tutte le attività' },
    { value: 'TURNI', label: 'Turni e servizi' },
    { value: 'INCARICHI', label: 'Incarichi' },
    { value: 'NOTIFICHE', label: 'Notifiche e promemoria' },
    { value: 'MANDATI', label: 'Mandati e relazioni' },
    { value: 'REPORT', label: 'Report' },
    { value: 'MEDIA', label: 'Foto, video e file' },
    { value: 'CLIENTI', label: 'Clienti' },
    { value: 'CONTABILITA', label: 'Contabilità e spese' },
    { value: 'PREVENTIVI', label: 'Preventivi' },
    { value: 'ACCESSI', label: 'Accessi e utenti' },
    { value: 'SISTEMA', label: 'Operazioni di sistema' },
];

const AuditLogsView: React.FC = () => {
    const [logs, setLogs] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedLog, setSelectedLog] = useState<any>(null);
    const [mostraTecnici, setMostraTecnici] = useState(false);
    const [loading, setLoading] = useState(false);

    // Il testo della persona va al server solo quando si smette di scrivere.
    const [personaInput, setPersonaInput] = useState('');
    const [persona, setPersona] = useState('');
    const [categoria, setCategoria] = useState('');
    const [rifTipo, setRifTipo] = useState('t');
    const [rifIdInput, setRifIdInput] = useState('');
    const [rif, setRif] = useState<{ tipo: string; id: number; etichetta?: string } | null>(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        const t = setTimeout(() => {
            const v = personaInput.trim();
            setPersona(v.length >= 2 ? v : '');
            setCurrentPage(0);
        }, 400);
        return () => clearTimeout(t);
    }, [personaInput]);

    useEffect(() => {
        const t = setTimeout(() => {
            const n = parseInt(rifIdInput, 10);
            setRif(prev => {
                if (!rifIdInput.trim()) return prev && prev.etichetta ? prev : null;
                return Number.isFinite(n) && n > 0 ? { tipo: rifTipo, id: n } : null;
            });
            setCurrentPage(0);
        }, 400);
        return () => clearTimeout(t);
    }, [rifIdInput, rifTipo]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getAuditLogs(currentPage, 20, {
                persona: persona || undefined,
                categoria: categoria || undefined,
                rifTipo: rif?.tipo,
                rifId: rif?.id,
                from: fromDate ? `${fromDate}T00:00:00` : undefined,
                to: toDate ? `${toDate}T23:59:59` : undefined,
            });
            setLogs(response.data.content);
            setTotalPages(response.data.totalPages);
            setTotalElements(response.data.totalElements);
        } catch (error) {
            console.error("Errore nel caricamento dei log", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, persona, categoria, rif, fromDate, toDate]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setter(e.target.value);
        setCurrentPage(0);
    };

    // Clic su un riferimento: tutto ciò che riguarda quel turno, incarico, mandato o persona
    // (per id, così vale anche per chi è stato eliminato e ha solo le iniziali).
    const filtraPer = (r: Riferimento) => {
        setSelectedLog(null);
        if (r.tipo !== 'u') setRifTipo(r.tipo);
        setRifIdInput('');
        setRif({ tipo: r.tipo, id: r.id, etichetta: r.etichetta });
        setCurrentPage(0);
    };

    const azzera = () => {
        setPersonaInput(''); setPersona(''); setCategoria('');
        setRifIdInput(''); setRif(null);
        setFromDate(''); setToDate(''); setCurrentPage(0);
    };
    const filtriAttivi = !!(personaInput || categoria || rif || rifIdInput || fromDate || toDate);

    const nomeAttore = (log: any) => {
        if (log.actorName === 'Sistema' || (log.actor || '').startsWith('SYSTEM')) return 'Sistema (automatico)';
        return log.actorName || log.actor || '—';
    };

    const getActionBadge = (log: any) => {
        const a = (log.action || '').toUpperCase();
        const label = log.actionLabel || log.action || '';
        if (a === 'NOTIFICA_INVIATA') return <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">{label}</span>;
        if (a.includes('NON_INVIATA') || a.includes('NON_RECAPITATA') || a.includes('FALLIT') || a.includes('ERROR')) return <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">{label}</span>;
        if (a.includes('DELET') || a.includes('ELIMIN') || a.includes('REVOC') || a.includes('RIFIUT')) return <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">{label}</span>;
        if (a.includes('UPDATE') || a.includes('AGGIOR') || a.includes('MODIF')) return <span className="px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200">{label}</span>;
        if (a.includes('CREATE') || a.includes('CREAT') || a.includes('NUOV') || a.includes('AGGIUNGI') || a.includes('PIANIFICAT') || a.includes('ACCETTAT')) return <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">{label}</span>;
        return <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">{label}</span>;
    };

    const etichettaRif = (r: { tipo: string; id: number; etichetta?: string }) =>
        r.etichetta || `${TIPI_RIF.find(t => t.value === r.tipo)?.label || 'Elemento'} n. ${r.id}`;

    return (
        <div className="p-4 lg:p-6 xl:p-8 space-y-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                        <Terminal className="text-blue-600" size={28} />
                        Registro Attività (Audit)
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Chi ha fatto cosa e quando • {totalElements} eventi
                    </p>
                </div>
                <button
                    onClick={fetchLogs}
                    className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl shadow-sm hover:bg-gray-50 transition-all font-medium text-sm"
                >
                    <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} /> Aggiorna
                </button>
            </div>

            {/* Barra Filtri */}
            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-3">
                <div className="flex flex-wrap gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Nome o cognome (es. collaboratore)"
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={personaInput}
                            onChange={e => setPersonaInput(e.target.value)}
                        />
                    </div>
                    <div className="relative min-w-[200px]">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <select
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={categoria}
                            onChange={handleFilterChange(setCategoria)}
                        >
                            {CATEGORIE.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div className="flex items-center gap-2 min-w-[220px]">
                        <Hash size={16} className="text-gray-400 shrink-0" />
                        <select
                            className="px-2 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            value={rifTipo}
                            onChange={e => setRifTipo(e.target.value)}
                        >
                            {TIPI_RIF.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input
                            type="number"
                            min={1}
                            placeholder="n."
                            className="w-24 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            value={rifIdInput}
                            onChange={e => setRifIdInput(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2 min-w-[220px]">
                        <Calendar size={16} className="text-gray-400 shrink-0" />
                        <input
                            type="date"
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={fromDate}
                            onChange={handleFilterChange(setFromDate)}
                        />
                        <span className="text-gray-400 text-xs">→</span>
                        <input
                            type="date"
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                            value={toDate}
                            onChange={handleFilterChange(setToDate)}
                        />
                    </div>
                    {filtriAttivi && (
                        <button
                            onClick={azzera}
                            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                        >
                            Azzera filtri
                        </button>
                    )}
                </div>
                {rif && (
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Tutto su:</span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                            {etichettaRif(rif)}
                            <button onClick={() => { setRif(null); setRifIdInput(''); setCurrentPage(0); }} className="hover:text-blue-900" aria-label="Togli filtro">
                                <X size={14} />
                            </button>
                        </span>
                    </div>
                )}
                <p className="text-xs text-gray-400">
                    Cercando una persona trovi sia ciò che ha fatto sia ciò che la riguarda: turni e incarichi assegnati, notifiche ricevute.
                    Clicca un turno o un mandato nel dettaglio per vederne tutta la storia.
                </p>
            </div>

            {/* Tabella */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto -mx-2 px-2 lg:mx-0 lg:px-0">
                    <table className="w-full min-w-[900px] text-left border-collapse">
                        <thead>
                        <tr className="bg-gray-50/50 border-b border-gray-100">
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Data e Ora</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Chi</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Azione</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Riguarda</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Dettagli</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                        {loading ? (
                            <tr><td colSpan={5} className="py-20 text-center text-gray-400 italic">Caricamento in corso...</td></tr>
                        ) : logs.length === 0 ? (
                            <tr><td colSpan={5} className="py-20 text-center text-gray-400">Nessuna attività trovata.</td></tr>
                        ) : (
                            logs.map((log) => (
                                <tr key={log.id} className="hover:bg-gray-50/80 transition-colors group cursor-pointer" onClick={() => { setSelectedLog(log); setMostraTecnici(false); }}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Calendar size={14} className="text-gray-400" />
                                            {format(new Date(log.timestamp), 'dd MMM yyyy HH:mm', { locale: it })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 shrink-0 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">
                                                {nomeAttore(log).charAt(0).toUpperCase()}
                                            </div>
                                            <p className="text-sm font-semibold text-gray-900">{nomeAttore(log)}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">{getActionBadge(log)}</td>
                                    <td className="px-6 py-4 text-sm text-gray-700 max-w-[420px]">
                                        {log.descrizione || <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSelectedLog(log); setMostraTecnici(false); }}
                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            aria-label="Dettagli"
                                        >
                                            <Eye size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Paginazione */}
                <div className="px-6 py-4 border-top border-gray-50 flex items-center justify-between bg-gray-50/30">
                    <span className="text-sm text-gray-500 font-medium">
                        Pagina {currentPage + 1} di {Math.max(totalPages, 1)}
                    </span>
                    <div className="flex gap-2">
                        <button
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            disabled={currentPage >= totalPages - 1}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-all"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal Dettagli */}
            {selectedLog && (
                <Modal
                    title="Dettagli Attività"
                    onClose={() => setSelectedLog(null)}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <div><p className="text-gray-500">Azione</p><p className="font-bold">{selectedLog.actionLabel || selectedLog.action}</p></div>
                            <div><p className="text-gray-500">Data</p><p>{format(new Date(selectedLog.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: it })}</p></div>
                            <div className="col-span-2">
                                <p className="text-gray-500">Chi</p>
                                {selectedLog.actorId ? (
                                    <button
                                        onClick={() => filtraPer({ tipo: 'u', id: selectedLog.actorId, etichetta: nomeAttore(selectedLog) })}
                                        className="font-semibold text-blue-700 hover:underline text-left"
                                    >
                                        {nomeAttore(selectedLog)}
                                    </button>
                                ) : <p className="font-semibold">{nomeAttore(selectedLog)}</p>}
                            </div>
                        </div>

                        {selectedLog.riferimenti?.length > 0 && (
                            <div>
                                <p className="text-sm font-bold text-gray-700 mb-2">Riguarda <span className="font-normal text-gray-400">(clicca per vedere tutta la storia)</span></p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedLog.riferimenti.map((r: Riferimento) => (
                                        <button
                                            key={`${r.tipo}${r.id}`}
                                            onClick={() => filtraPer(r)}
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${r.eliminato
                                                ? 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                                : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'}`}
                                        >
                                            {r.etichetta}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedLog.campi?.length > 0 ? (
                            <div className="border border-gray-100 rounded-xl divide-y divide-gray-100">
                                {selectedLog.campi.map((c: Campo, i: number) => (
                                    <div key={i} className="grid grid-cols-[minmax(120px,40%)_1fr] gap-3 px-4 py-2 text-sm">
                                        <span className="text-gray-500">{c.etichetta}</span>
                                        <span className="text-gray-900 break-words">{c.valore}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400">Nessun altro dettaglio.</p>
                        )}

                        <div>
                            <button
                                onClick={() => setMostraTecnici(v => !v)}
                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                                <ChevronDown size={14} className={`transition-transform ${mostraTecnici ? 'rotate-180' : ''}`} />
                                Dati tecnici
                            </button>
                            {mostraTecnici && (
                                <div className="mt-2 space-y-2">
                                    <p className="text-xs text-gray-500">Codice azione: <span className="font-mono">{selectedLog.action}</span> · Utente: <span className="font-mono">{selectedLog.actor}</span> · IP: <span className="font-mono">{selectedLog.ipAddress || '—'}</span></p>
                                    <div className="bg-slate-900 rounded-xl p-4 overflow-auto max-h-[240px]">
                                        <pre className="text-blue-400 text-xs font-mono">
                                            {JSON.stringify(selectedLog.details ?? {}, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setSelectedLog(null)}
                            className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-all"
                        >
                            Chiudi
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AuditLogsView;
